import type { ProblemRecord, WorkflowStage } from "@/lovable-demo/lib/warranty/types";
import type { ReadinessAssessment, ReadinessStatus } from "./types";

/**
 * Data Quality & Decision Readiness foundation.
 *
 * Pure, deterministic scoring used by the governance layer only. It does not
 * change any existing calculation, and it is deliberately not surfaced in the
 * UI in Package #1 — the screens stay as they are.
 */

function statusFor(score: number, gateSatisfied: boolean): ReadinessStatus {
  if (!gateSatisfied) return "not-ready";
  if (score >= 85) return "ready";
  if (score >= 60) return "caution";
  return "not-ready";
}

export function assessStageReadiness(
  record: ProblemRecord,
  stage: WorkflowStage | undefined,
): ReadinessAssessment {
  if (!stage) {
    return {
      score: 0,
      status: "unknown",
      gateSatisfied: false,
      evidenceCount: 0,
      requiredEvidenceCount: 0,
      findings: ["Stage not found."],
    };
  }

  const findings: string[] = [];
  const evidenceCount = stage.evidence.length;
  const requiredEvidenceCount = stage.requiredEvidence.length;
  const gateSatisfied = evidenceCount >= requiredEvidenceCount;

  let score = 100;

  if (!gateSatisfied) {
    const missing = requiredEvidenceCount - evidenceCount;
    score -= Math.min(60, missing * 25);
    findings.push(`${missing} required evidence item(s) outstanding.`);
  }

  const undocumented = stage.evidence.filter((e) => !e.summary?.trim()).length;
  if (undocumented > 0) {
    score -= undocumented * 5;
    findings.push(`${undocumented} evidence item(s) lack a provenance summary.`);
  }

  if (!record.c1000?.length) {
    score -= 10;
    findings.push("No claims-per-1,000 series available for this record.");
  }

  if (!record.supplier?.trim()) {
    score -= 5;
    findings.push("No supplier linked to the record.");
  }

  if (!record.pfmea?.length) {
    score -= 10;
    findings.push("No PFMEA rows recorded.");
  }

  const clauseFindings = (record.clauses ?? []).filter((c) => c.verdict !== "Compliant").length;
  if (clauseFindings > 0) findings.push(`${clauseFindings} open clause finding(s).`);

  score = Math.max(0, Math.min(100, Math.round(score)));

  return {
    score,
    status: statusFor(score, gateSatisfied),
    gateSatisfied,
    evidenceCount,
    requiredEvidenceCount,
    findings,
  };
}

export function assessRecordReadiness(record: ProblemRecord): ReadinessAssessment {
  const active =
    record.stages.find((s) => s.status === "in-progress") ??
    record.stages.find((s) => s.status !== "complete") ??
    record.stages[record.stages.length - 1];
  return assessStageReadiness(record, active);
}
